import React, { CSSProperties, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { css, StyledProps } from 'styled-components'
import { useTempBoolean } from '../../../hooks'

export type TooltipPosition = 'top' | 'left' | 'right' | 'bottom'

const Container = styled.div`
  font-size: 14px;
  line-height: 20px;
  position: relative;
`

const positionCss = ({ position }: StyledProps<{ position: TooltipPosition }>) => {
  const offset = '8px'
  switch (position) {
    case 'top':
      return css`
        left: 50%;
        top: 0;
        transform: translate(-50%, calc(-100% - ${offset}));
      `
    case 'right':
      return css`
        right: 0;
        top: 50%;
        transform: translate(calc(100% + ${offset}), -50%);
      `
    case 'bottom':
      return css`
        left: 50%;
        bottom: 0;
        transform: translate(-50%, calc(100% + ${offset}));
      `
    case 'left':
      return css`
        left: 0;
        top: 50%;
        transform: translate(calc(-100% - ${offset}), -50%);
      `
  }
}

const TooltipContainer = styled.div<{ position: TooltipPosition }>`
  position: absolute;
  background-color: ${props => props.theme.color.primary100};
  opacity: 0.96;
  border-radius: 8px;
  padding: 4px 8px;
  z-index: ${props => props.theme.zIndex.tooltip};
  ${positionCss}
`

type Coords = {
  top: number
  left: number
}

type PortalProps = {
  mount: HTMLElement
  coords: Coords
  children?: React.ReactNode
}

export const Tooltip: React.FC<{
  visibleOnHover: boolean
  visible?: boolean
  tooltipContent: React.ReactNode
  className?: string
  position?: TooltipPosition
  temporary?: boolean
  timeout?: number
  tooltipContainerClassName?: string
  portalProps?: PortalProps
  style?: CSSProperties
  children?: React.ReactNode
}> = ({
  children,
  tooltipContent,
  className,
  visible = true,
  position = 'top',
  temporary,
  visibleOnHover,
  timeout = 1000,
  portalProps,
  style = {},
}) => {
  const [hovered, setHovered] = useState(false)

  const handleMouseEnter = () => {
    setHovered(true)
  }
  const handleMouseLeave = () => {
    setHovered(false)
  }

  const isTooltipVisible = useTempBoolean(hovered, timeout, !temporary || (hovered && visibleOnHover)) && visible
  return (
    <Container className={className} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
      {isTooltipVisible && !!tooltipContent ? (
        !portalProps ? (
          <TooltipContainer position={position} style={style}>
            {tooltipContent}
          </TooltipContainer>
        ) : (
          <Portal {...portalProps}>
            <TooltipContainer position={position} style={style}>
              {tooltipContent}
            </TooltipContainer>
          </Portal>
        )
      ) : null}
    </Container>
  )
}

const Portal: React.FC<PortalProps> = ({ children, coords, mount }) => {
  const el = React.useMemo(() => document.createElement('div'), [])

  useEffect(() => {
    if (mount) {
      mount.appendChild(el)
      return () => {
        mount.removeChild(el)
      }
    }
  }, [el, mount])

  return createPortal(<div style={{ position: 'absolute', top: coords.top, left: coords.left }}>{children}</div>, el)
}
